import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

interface ArgoCDProject {
  entityRef: string;
  name: string;
  title: string;
  instanceName: string;
  projectName: string;
  description: string;
  destinations: string;
}

/**
 * ArgoCDProjectPicker
 *
 * 自定义字段：从 Backstage Catalog 动态加载 ArgoCD Project 列表
 *
 * 使用方式：
 * ```yaml
 * argocdProject:
 *   title: ArgoCD Project
 *   type: string
 *   ui:field: ArgoCDProjectPicker
 * ```
 *
 * 特点：
 * - 从 Catalog API 获取所有 type=argocd-project 的 Resource 实体
 * - 显示项目名称、实例名和描述
 * - 返回实体的完整 entityRef (resource:default/argocd-xxx)
 */
export const ArgoCDProjectPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  idSchema,
  schema,
}: FieldProps<string>) => {
  const catalogApi = useApi(catalogApiRef);

  const [projects, setProjects] = useState<ArgoCDProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 从 Catalog 获取所有 ArgoCD Project 实体
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': 'argocd-project',
          },
        });

        const projectList: ArgoCDProject[] = response.items.map(entity => {
          const annotations = entity.metadata.annotations || {};
          return {
            entityRef: `resource:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`,
            name: entity.metadata.name,
            title: entity.metadata.title || entity.metadata.name,
            instanceName: annotations['argocd.argoproj.io/instance-name'] || '',
            projectName: annotations['argocd.argoproj.io/project-name'] || '',
            description: entity.metadata.description || '',
            destinations: annotations['hose.io/destinations'] || '[]',
          };
        });

        // 按 instanceName 和 projectName 排序
        projectList.sort((a, b) => {
          const instanceCompare = a.instanceName.localeCompare(b.instanceName);
          if (instanceCompare !== 0) return instanceCompare;
          return a.projectName.localeCompare(b.projectName);
        });

        setProjects(projectList);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load ArgoCD projects');
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [catalogApi]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || 'ArgoCD Project'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && projects.length === 0 && (
          <MenuItem value="" disabled>
            无可用 ArgoCD Project（请等待同步或联系运维）
          </MenuItem>
        )}
        {projects.map(project => (
          <MenuItem key={project.entityRef} value={project.entityRef}>
            <strong>{project.projectName}</strong>
            <span style={{ color: '#666', marginLeft: 8 }}>
              @ {project.instanceName}
            </span>
            {project.description && (
              <span style={{ color: '#999', marginLeft: 8 }}>
                - {project.description}
              </span>
            )}
          </MenuItem>
        ))}
      </Select>
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>请选择一个 ArgoCD Project</FormHelperText>
      )}
      {formData && (
        <FormHelperText>
          已选择: {projects.find(p => p.entityRef === formData)?.title || formData}
        </FormHelperText>
      )}
    </FormControl>
  );
};

